"use client";

import type { EnsureWebPlayerReadyResult } from "./webPlayerContract";
import type {
  RequiredWebPlayerTrackDraft,
  WebPlayerTrackDraft,
  WebPlayerTrackMeta,
} from "./webPlayerTypes";

type WebPlayerSharedStoreListener = () => void;

type WebPlayerNamedArtist = {
  name?: string | null;
};

export type WebPlayerSharedSnapshot = {
  isReady: boolean;
  isPlaying: boolean;
  currentTrack: WebPlayerTrackMeta | null;
  deviceId: string | null;
  positionMs: number;
  durationMs: number;
  volume: number;
  error: string | null;
  needsTransfer: boolean;
};

export function createEnsureReadyResult(
  ready: boolean,
  deviceId: string | null,
  error: string | null
): EnsureWebPlayerReadyResult {
  return {
    ready,
    deviceId,
    error,
  };
}

export function clampWebPlayerVolume(next: number) {
  return Math.max(0, Math.min(1, next));
}

export function joinWebPlayerArtistNames(
  artists: WebPlayerNamedArtist[] | null | undefined
) {
  if (!Array.isArray(artists)) return "";
  return artists
    .map((artist) =>
      typeof artist?.name === "string" ? artist.name.trim() : ""
    )
    .filter(Boolean)
    .join(", ");
}

export function createWebPlayerTrackMeta({
  id,
  uri,
  title,
  artist,
  imageUrl = null,
  albumName = null,
  durationMs = null,
  previewUrl = null,
  isRestricted = null,
  restrictionReason = null,
  restrictionReasons = null,
}: WebPlayerTrackDraft): WebPlayerTrackMeta | null {
  const normalizedId = typeof id === "string" ? id.trim() : "";
  const normalizedUri = typeof uri === "string" ? uri.trim() : "";
  const normalizedTitle = typeof title === "string" ? title.trim() : "";

  if (!normalizedId || !normalizedUri || !normalizedTitle) {
    return null;
  }

  return {
    id: normalizedId,
    uri: normalizedUri,
    title: normalizedTitle,
    artist: typeof artist === "string" ? artist.trim() : "",
    imageUrl:
      typeof imageUrl === "string" && imageUrl.trim() ? imageUrl.trim() : null,
    albumName:
      typeof albumName === "string" && albumName.trim() ? albumName.trim() : null,
    durationMs:
      typeof durationMs === "number" && Number.isFinite(durationMs)
        ? Math.max(0, Math.trunc(durationMs))
        : null,
    previewUrl:
      typeof previewUrl === "string" && previewUrl.trim()
        ? previewUrl.trim()
        : null,
    isRestricted: typeof isRestricted === "boolean" ? isRestricted : null,
    restrictionReason:
      typeof restrictionReason === "string" && restrictionReason.trim()
        ? restrictionReason.trim()
        : null,
    restrictionReasons:
      Array.isArray(restrictionReasons) && restrictionReasons.length > 0
        ? restrictionReasons
            .map((entry) =>
              typeof entry === "string" && entry.trim() ? entry.trim() : ""
            )
            .filter(Boolean)
        : null,
  };
}

export function createRequiredWebPlayerTrackMeta({
  id,
  uri,
  title,
  artist,
  imageUrl = null,
  albumName = null,
  durationMs = null,
  previewUrl = null,
  isRestricted = null,
  restrictionReason = null,
  restrictionReasons = null,
}: RequiredWebPlayerTrackDraft): WebPlayerTrackMeta {
  return (
    createWebPlayerTrackMeta({
      id,
      uri,
      title,
      artist,
      imageUrl,
      albumName,
      durationMs,
      previewUrl,
      isRestricted,
      restrictionReason,
      restrictionReasons,
    }) ?? {
      id,
      uri,
      title,
      artist,
      imageUrl: imageUrl ?? null,
      albumName: albumName ?? null,
      durationMs: durationMs ?? null,
      previewUrl: previewUrl ?? null,
      isRestricted: isRestricted ?? null,
      restrictionReason: restrictionReason ?? null,
      restrictionReasons: restrictionReasons ?? null,
    }
  );
}

export function createWebPlayerSharedStore<TState extends Record<string, unknown>>(
  initialState: TState
) {
  let state = { ...initialState };
  const listeners = new Set<WebPlayerSharedStoreListener>();

  const publish = () => {
    for (const listener of Array.from(listeners)) {
      listener();
    }
  };

  return {
    getState() {
      return state;
    },
    patchState(patch: Partial<TState>) {
      state = {
        ...state,
        ...patch,
      };
      publish();
    },
    replaceState(nextState: TState) {
      state = { ...nextState };
      publish();
    },
    subscribe(listener: WebPlayerSharedStoreListener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
